/** @format */

import React, { ElementType, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
	selectAllCirclesOfMember,
	selectCurrentUser,
	selectIsAuthenticated,
} from "../../redux/selectors/userSelector";
import {
	circleListIncludes,
	userUnionsInclude,
} from "../../utility/utils/circle-membership";
import { RoleNames } from "../../utility/types/rolesType";
import { getUnauthRedirectPath } from "../../utility/redirectHelper";

interface ProtectedRouteProps {
	component: ElementType;
	path?: string;
	requiredRoles?: string[];
	circleId?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
	component: Component,
	requiredRoles,
	circleId,
	...rest
}) => {
	const isAuthChecked = useSelector(selectIsAuthenticated);

	const currentUser = useSelector(selectCurrentUser);
	const memberCircles = useSelector(selectAllCirclesOfMember);

	const navigate = useNavigate();

	// Member if either side of the membership says so (see circle-membership.ts):
	// the user's own circle list, or the circles the server lists for this member.
	const isCircleMember =
		!!circleId &&
		(userUnionsInclude(currentUser?.unions as any[], circleId) ||
			circleListIncludes(memberCircles?.circles as any[], circleId));

	useEffect(() => {
		if (!isAuthChecked) {
			navigate(getUnauthRedirectPath());
		} else if (currentUser?.isBanned) {
			// 🚨 CRITICAL: Redirect banned users to banned page
			navigate("/account-banned", {
				state: {
					bannedAt: currentUser.bannedAt,
					banReason: currentUser.banReason,
				},
			});
		} else {
			const hasRequiredRole =
				!requiredRoles || requiredRoles.length === 0
					? true
					: currentUser?.roles?.some((role) => {
						return requiredRoles?.includes(role.roleName);
					});

			const isAdmin = currentUser?.roles?.some(
				(r) =>
					r.roleName === RoleNames.SUPER_ADMIN || r.roleName === RoleNames.ADMIN
			);

			// if (hasRequiredRole && isAdmin && window.location.pathname !== "/admin") {
			// 	navigate("/admin");
			// 	return;
			// }

			// Allow access if the user is authenticated and either has a required role or is a member of the circle
			const isAuthorized =
				(requiredRoles ? hasRequiredRole : true) || isCircleMember;

			if (!isAuthorized) {
				navigate(`/user/${currentUser?._id}`);
			}
		}
	}, [isAuthChecked, currentUser, navigate, requiredRoles, circleId, isCircleMember]);

	return isAuthChecked &&
		currentUser &&
		((requiredRoles
			? currentUser.roles?.some((role) => requiredRoles.includes(role.roleName))
			: true) ||
			isCircleMember) ? (
		<Component {...rest} />
	) : null;
};
